import { Box, Typography, useTheme } from "@mui/material";
import * as React from "react";

import { useNavigation } from "@/providers/navigation";
import { RobotoButton } from "@/shared/components";
import {
  AnnotationViewFilters,
  Annotations,
} from "@/shared/components/Annotations";
import {
  FileRecord,
  IngestionStatus,
  MAX_FILE_SIZE_PREVIEW,
} from "@/shared/domain/files/FileRecord";
import {
  isImage,
  isText,
  isMarkdown,
  isVideo,
  isWebRenderable,
  canHaveTopics,
} from "@/shared/domain/files/fileTypes";
import { useSignedUrl } from "@/shared/domain/files/hooks/useFile";

import { IframeContent } from "./IframeContent";
import { ImageContent } from "./ImageContent";
import { TextContent } from "./TextContent";
import { VideoContent } from "./VideoContent";

interface FileContentProps {
  fileRecord: FileRecord;
  signedUrl?: URL;
  annotationData?: Annotations;
  annotationViewFilters?: AnnotationViewFilters;
}

export const FileContent: React.FC<FileContentProps> = ({
  fileRecord,
  annotationData,
  annotationViewFilters,
}) => {
  const theme = useTheme();
  const { goto } = useNavigation();

  const signedUrlQuery = useSignedUrl(fileRecord);

  const msgContent = (message: React.ReactElement) => {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            maxWidth: "500px",
            padding: theme.spacing(3),
            backgroundColor: theme.palette.code.main,
          }}
        >
          {message}
        </Typography>
      </Box>
    );
  };

  let contentType;

  if (isImage(fileRecord.relative_path)) {
    contentType = (
      <ImageContent
        fileRecord={fileRecord}
        signedUrl={signedUrlQuery.data}
        annotationData={annotationData}
        annotationViewFilters={annotationViewFilters}
      />
    );
  } else if (isVideo(fileRecord.relative_path)) {
    contentType = (
      <VideoContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
    );
  } else if (
    isText(fileRecord.relative_path) ||
    isMarkdown(fileRecord.relative_path)
  ) {
    if (fileRecord.size > MAX_FILE_SIZE_PREVIEW) {
      contentType = msgContent(
        <>
          File size is over 50 MB, skipping preview <br />
          <span
            style={{
              fontSize: "0.9rem",
              color: theme.palette.text.secondary,
            }}
          >
            Open or Download the file using the buttons above
          </span>
        </>,
      );
    } else {
      contentType = (
        <TextContent fileRecord={fileRecord} signedUrl={signedUrlQuery.data} />
      );
    }
  } else if (isWebRenderable(fileRecord.relative_path)) {
    contentType = <IframeContent signedUrl={signedUrlQuery.data} />;
  } else if (fileRecord.ingestion_status !== IngestionStatus.NotIngested) {
    contentType = msgContent(
      <>
        No preview available for this file type.
        <br />
        <span
          style={{
            fontSize: "0.9rem",
            color: theme.palette.text.secondary,
          }}
        >
          <RobotoButton
            eventName={"VisualizeFileClicked"}
            variant={"text"}
            color="primary"
            onClick={() => {
              goto.visualize({
                dataSource: {
                  files: [fileRecord.file_id],
                },
              });
            }}
            eventProperties={{
              fileId: fileRecord.file_id,
            }}
          >
            Open in Visualizer
          </RobotoButton>
        </span>
      </>,
    );
  } else if (canHaveTopics(fileRecord.relative_path)) {
    contentType = msgContent(
      <>
        File not ingested yet
        <br />
        <span
          style={{
            fontSize: "0.9rem",
            color: theme.palette.text.secondary,
          }}
        >
          This file hasn&apos;t been ingested, so there aren&apos;t any topics
          available for visualization. Check if any ingestion actions are
          currently running on its dataset, or invoke one manually.
        </span>
      </>,
    );
  } else {
    contentType = msgContent(
      <>
        No preview available <br />
        <span
          style={{
            fontSize: "0.9rem",
            color: theme.palette.text.secondary,
          }}
        >
          Open or Download the file using the buttons above
        </span>
      </>,
    );
  }

  return contentType;
};
